import type { Phoneme } from "./types";

/* Canonical phoneme targets for the Sound Map. Every twister's raw `focus`
   tag gets parsed and normalized down to these ids (see ALIASES below) —
   consonant clusters decompose into their constituent articulators rather
   than each getting their own map cell, so "BR" trains B and R, not a
   third "BR" sound that doesn't otherwise exist on the map. */
export const PHONEMES: Phoneme[] = [
  // ---- Lips ----
  { id: "P", label: "P", family: "lips", diagramId: "lips", cue: "Press your lips together, then pop them open with a burst of air. Voiceless — no hum." },
  { id: "B", label: "B", family: "lips", diagramId: "lips", cue: "Same lip-press as P, but voiced — feel the buzz in your throat as it pops." },
  { id: "W", label: "W", family: "lips", diagramId: "lips-round", cue: "Round your lips into a tight circle, then glide open into the next vowel." },

  // ---- Nasal ----
  { id: "M", label: "M", family: "nasal", diagramId: "lips", cue: "Close your lips and hum through your nose. Fully voiced, no release." },
  { id: "N", label: "N", family: "nasal", diagramId: "tip-alveolar", cue: "Press your tongue tip to the ridge behind your teeth and hum through your nose." },
  { id: "NG", label: "NG", family: "nasal", diagramId: "back-velar", cue: "Press the back of your tongue up and hum through your nose, like the end of \"sing\"." },

  // ---- Tongue tip ----
  { id: "T", label: "T", family: "tip", diagramId: "tip-alveolar", cue: "Press your tongue tip behind your top teeth, then release with a crisp click. Voiceless." },
  { id: "D", label: "D", family: "tip", diagramId: "tip-alveolar", cue: "Same tongue placement as T, but voiced — a softer, buzzing release." },
  { id: "L", label: "L", family: "tip", diagramId: "tip-alveolar", cue: "Touch your tongue tip to the ridge behind your teeth and let air flow around the sides." },
  { id: "R", label: "R", family: "tip", diagramId: "tip-retroflex", cue: "Curl or bunch your tongue without touching the roof of your mouth, then glide." },
  { id: "Y", label: "Y", family: "tip", diagramId: "palatal", cue: "Arch the middle of your tongue toward the roof of your mouth and glide into the vowel." },

  // ---- Back ----
  { id: "K", label: "K", family: "back", diagramId: "back-velar", cue: "Press the back of your tongue against your soft palate, then snap it off. Voiceless." },
  { id: "G", label: "G", family: "back", diagramId: "back-velar", cue: "Same back-of-tongue placement as K, but voiced — a deeper, buzzing pop." },

  // ---- Fricatives (and CH, the closest affricate) ----
  { id: "F", label: "F", family: "fricative", diagramId: "labiodental", cue: "Rest your top teeth on your bottom lip and push air through, no voice." },
  { id: "V", label: "V", family: "fricative", diagramId: "labiodental", cue: "Same as F, but voiced — buzz your throat while the air hisses through." },
  { id: "S", label: "S", family: "fricative", diagramId: "tip-alveolar", cue: "Bring your tongue close to the ridge behind your teeth and hiss — steady, voiceless air." },
  { id: "Z", label: "Z", family: "fricative", diagramId: "tip-alveolar", cue: "Same tongue position as S, but voiced — a buzzing hiss." },
  { id: "SH", label: "SH", family: "fricative", diagramId: "palatal", cue: "Round your lips slightly and push air over your tongue for a soft \"shh\"." },
  { id: "CH", label: "CH", family: "fricative", diagramId: "palatal", cue: "Start with a T, then release into SH in one quick burst." },
  { id: "TH", label: "TH", family: "fricative", diagramId: "dental", cue: "Place your tongue tip lightly between your teeth and push air through — no bite." },
  { id: "H", label: "H", family: "fricative", diagramId: "glottal", cue: "Relax your throat and let air pass through, unshaped — a plain, breathy exhale." },

  // ---- Swedish specials ----
  { id: "SJ", label: "SJ", family: "swedish", diagramId: "palatal", cue: "Round your lips and push air through a narrow channel further back than SH — Swedish's signature hush." },
  { id: "TJ", label: "TJ", family: "swedish", diagramId: "palatal", cue: "Arch your tongue high and forward, close to a soft \"ch\", but lighter and airier." },

  // ---- Vowels ----
  { id: "Å", label: "Å", family: "vowel", diagramId: "vowel-round", cue: "Round your lips and drop your jaw for a deep, rounded \"oh\"." },
  { id: "Ä", label: "Ä", family: "vowel", diagramId: "vowel-open", cue: "Open wide with a relaxed jaw for a bright \"eh\", longer than English \"e\"." },
  { id: "Ö", label: "Ö", family: "vowel", diagramId: "vowel-round", cue: "Round your lips as if for \"oo\" but say \"eh\" — the classic Swedish front-rounded vowel." },
  { id: "OY", label: "OY", family: "vowel", diagramId: "vowel-open", cue: "Glide from an open \"aw\" into a tight \"ee\" in one smooth diphthong." },
];

export const PHONEME_IDS = new Set(PHONEMES.map((p) => p.id));

/** Raw twister `focus` tokens that aren't themselves canonical phonemes —
    clusters and digraphs decompose into their constituent articulators. */
export const PHONEME_ALIASES: Record<string, string[]> = {
  BJ: ["B"],
  BR: ["B", "R"],
  BL: ["B", "L"],
  FL: ["F", "L"],
  GR: ["G", "R"],
  KR: ["K", "R"],
  KN: ["K", "N"],
  TR: ["T", "R"],
  FR: ["F", "R"],
  TW: ["T", "W"],
  ST: ["S", "T"],
  STR: ["S", "T", "R"],
  SK: ["S", "K"],
  SV: ["S", "V"],
  KV: ["K", "V"],
  KS: ["K", "S"],
  NK: ["N", "K"],
  C: ["S"],
  EER: ["R"],
};

/** Parses a raw legacy `focus` tag ("S · SH", "P·B·K", "R · EER", …) into
    canonical phoneme ids. Throws on anything unrecognized — an unmapped
    tag should fail loudly, not silently vanish from the Sound Map. */
export function parseFocusTag(raw: string): string[] {
  const tokens = raw
    .split(/[·\s]+/)
    .map((t) => t.trim())
    .filter(Boolean);
  const out = new Set<string>();
  for (const token of tokens) {
    const upper = token.toUpperCase();
    if (PHONEME_IDS.has(upper)) {
      out.add(upper);
      continue;
    }
    const alias = PHONEME_ALIASES[upper];
    if (alias) {
      for (const id of alias) out.add(id);
      continue;
    }
    throw new Error(`parseFocusTag: unmapped phoneme token "${token}" in "${raw}"`);
  }
  return [...out];
}
